import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalBody, ModalHeader, ModalFooter, ModalOverlay } from "@chakra-ui/modal";
import { useState } from "react";
import { deleteWhiteListRecord } from "api/whiteListed";
import toast from "react-hot-toast";

const DeleteModal = ({
  id,
  fetchListData,
  heading,
  message,
  handleDelete,
  isDeleteLoading,
}: {
  name: string;
  id: string;
  fetchListData: () => void;
  heading: string;
  message: string;
  handleDelete: any;
  isDeleteLoading: any;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCloseModal, setIsCloseModal] = useState<boolean>(false);
  const [isDeleteLoadingData, setIsDeleteLoadingData] = useState<boolean>(false);

  const handleDeleteData = (deleteId: string) => {
    setIsDeleteLoadingData(true);
    deleteWhiteListRecord(deleteId)
      .then((data) => { handleClose(); fetchListData(); toast.success(data?.message || "Record deleted"); })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Record deletion failed"))
      .finally(() => setIsDeleteLoadingData(false));
  };

  const handleClose = () => {
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  return (
    <>
      <button className="dm-delete-trigger" onClick={onOpen} title="Delete record">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
          <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
        </svg>
      </button>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay  style={{ backdropFilter : "blur(4px)", background: "rgba(15,23,42,.55)" }}  />
        <div className={`dm-panel${isOpen && !isCloseModal ? " dm-panel--open" : " dm-panel--closed"}`}>
          <div className="dm-panel-header">
            <button className="dm-back-btn" onClick={handleClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <div>
              <h2 className="dm-panel-title">{heading}</h2>
              <p className="dm-panel-sub">This action cannot be undone</p>
            </div>
          </div>

          <div className="dm-panel-body">
            <div className="dm-warning-card">
              <div className="dm-warning-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div>
                <p className="dm-warning-title">Confirm Deletion</p>
                <p className="dm-warning-msg">{message}</p>
              </div>
            </div>

            <div className="dm-detail-card">
              <div className="dm-detail-row">
                <span className="dm-detail-label">Record ID</span>
                <code className="dm-detail-code">{id}</code>
              </div>
            </div>
          </div>

          <div className="dm-panel-footer">
            <button className="dm-btn dm-btn--ghost" onClick={handleClose}>Cancel</button>
            <button className="dm-btn dm-btn--danger" onClick={() => handleDeleteData(id)} disabled={isDeleteLoadingData}>
              {isDeleteLoadingData ? <span className="dm-btn-spinner" /> : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
                </svg>
              )}
              Delete Record
            </button>
          </div>
        </div>
      </Modal>

      <DmStyles />
    </>
  );
};

export default DeleteModal;

const DmStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    .dm-delete-trigger { width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #fee2e2; background: #fff; display: inline-flex; align-items: center; justify-content: center; color: #ef4444; cursor: pointer; transition: all .15s; }
    .dm-delete-trigger:hover { background: #fef2f2; border-color: #fecaca; }

    .dm-panel { font-family: 'Plus Jakarta Sans', sans-serif; position: fixed; top: 0; right: 0; height: 100vh; width: 100%; max-width: 600px; background: #ffffff; z-index: 1004; display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(15,23,42,.12); transition: transform .3s cubic-bezier(.4,0,.2,1); }
    .dm-panel--open  { transform: translateX(0); }
    .dm-panel--closed { transform: translateX(100%); }

    .dm-panel-header { display: flex; align-items: center; gap: 16px; padding: 17px 28px; background: linear-gradient(135deg, #7f1d1d, #dc2626); flex-shrink: 0; }
    .dm-back-btn { width: 36px; height: 36px; border-radius: 10px; background: rgba(255,255,255,.18); border: 1px solid rgba(255,255,255,.25); display: flex; align-items: center; justify-content: center; color: #fff; cursor: pointer; transition: background .15s; flex-shrink: 0; }
    .dm-back-btn:hover { background: rgba(255,255,255,.28); }
    .dm-panel-title { font-size: 17px; font-weight: 700; color: #fff; letter-spacing: -.3px;line-height:22px }
    .dm-panel-sub { font-size: 12px; color: rgba(255,255,255,.7); font-weight: 500; margin-top: 2px; }

    .dm-panel-body { flex: 1; overflow-y: auto; padding: 28px; display: flex; flex-direction: column; gap: 16px; }
    .dm-panel-footer { padding: 20px 28px; border-top: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: flex-end; gap: 10px; flex-shrink: 0; background: #fafbfc; }

    .dm-warning-card { display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 14px; }
    .dm-warning-icon { width: 48px; height: 48px; border-radius: 12px; background: #fef3c7; border: 1.5px solid #fde68a; display: flex; align-items: center; justify-content: center; color: #d97706; flex-shrink: 0; }
    .dm-warning-title { font-size: 14px; font-weight: 700; color: #92400e; margin-bottom: 6px; }
    .dm-warning-msg { font-size: 13px; color: #78350f; font-weight: 500; line-height: 1.6; }

    .dm-detail-card { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 16px 20px; }
    .dm-detail-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .dm-detail-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .4px; }
    .dm-detail-code { font-family: 'Courier New', monospace; font-size: 12.5px; font-weight: 600; color: #1e293b; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 4px 10px; }

    .dm-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; cursor: pointer; border: none; transition: all .15s; }
    .dm-btn--ghost { background: #f1f5f9; color: #64748b; border: 1.5px solid #e2e8f0; }
    .dm-btn--ghost:hover { background: #e2e8f0; }
    .dm-btn--danger { background: linear-gradient(135deg,#991b1b,#dc2626); color: #fff; box-shadow: 0 2px 8px rgba(220,38,38,.3); }
    .dm-btn--danger:hover:not(:disabled) { background: linear-gradient(135deg,#7f1d1d,#991b1b); box-shadow: 0 4px 16px rgba(220,38,38,.4); }
    .dm-btn--danger:disabled { opacity: .65; cursor: not-allowed; }
    .dm-btn-spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: dmSpin .7s linear infinite; }
    @keyframes dmSpin { to { transform: rotate(360deg); } }
  `}</style>
);